import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { IGlobalSearchResultDto, ISearchResultDto } from '../../../model/base/response/search-response';

interface SearchState {
  results: ISearchResultDto[];
  totalRecords: number;
  pageCount: number;
  totalByType: { [key: string]: number };
  searchTerm: string;
  loading: boolean;
  error: string | null;
}

type SearchAction =
  | { type: 'SEARCH_START' }
  | { type: 'SEARCH_SUCCESS'; payload: IGlobalSearchResultDto }
  | { type: 'SEARCH_ERROR'; payload: string }
  | { type: 'RESET_SEARCH' };

const initialState: SearchState = {
  results: [],
  totalRecords: 0,
  pageCount: 0,
  totalByType: {},
  searchTerm: '',
  loading: false,
  error: null,
};

function searchReducer(state: SearchState, action: SearchAction): SearchState {
  switch (action.type) {
    case 'SEARCH_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'SEARCH_SUCCESS':
      return {
        ...state,
        loading: false,
        results: action.payload.results?.items || [],
        totalRecords: action.payload.results?.totalRecords || 0,
        pageCount: action.payload.results?.pageCount || 0,
        totalByType: action.payload.totalByType || {},
        searchTerm: action.payload.searchTerm || '',
        error: null,
      };
    case 'SEARCH_ERROR':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case 'RESET_SEARCH':
      return initialState;
    default:
      return state;
  }
}

const SearchContext = createContext<SearchState | undefined>(undefined);
const SearchDispatchContext = createContext<React.Dispatch<SearchAction> | undefined>(undefined);

interface SearchProviderProps {
  children: ReactNode;
}

export function SearchProvider({ children }: SearchProviderProps) {
  const [state, dispatch] = useReducer(searchReducer, initialState);

  return (
    <SearchContext.Provider value={state}>
      <SearchDispatchContext.Provider value={dispatch}>
        {children}
      </SearchDispatchContext.Provider>
    </SearchContext.Provider>
  );
}

export function useSearchState() {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearchState must be used within a SearchProvider');
  }
  return context;
}

export function useSearchDispatch() {
  const context = useContext(SearchDispatchContext);
  if (context === undefined) {
    throw new Error('useSearchDispatch must be used within a SearchProvider');
  }
  return context;
}
